import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "wc-carousel-modern" }
const _hoisted_2 = { class: "wc-carousel-modern__viewport" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["disabled"]

import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

interface Props {
  title: string;
  titleId?: string;
  ariaLabel?: string;
  scrollAmount?: number;
  showArrows?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WCCarouselModern',
  props: {
    title: {},
    titleId: { default: '' },
    ariaLabel: { default: '' },
    scrollAmount: { default: 332 },
    showArrows: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props;

const trackRef = ref<HTMLElement | null>(null);
const atStart = ref(true);
const atEnd = ref(false);
// Plain variable is sufficient — scrollUpdateTimeout is never read in the template
let scrollUpdateTimeout: number | null = null;

const clearScrollUpdateTimeout = () => {
  if (scrollUpdateTimeout !== null) {
    window.clearTimeout(scrollUpdateTimeout);
    scrollUpdateTimeout = null;
  }
};

const updateScrollState = () => {
  if (!trackRef.value) {
    return;
  }

  const currentMaxScroll = Math.max(trackRef.value.scrollWidth - trackRef.value.clientWidth, 0);

  atStart.value = trackRef.value.scrollLeft <= 1;
  atEnd.value = trackRef.value.scrollLeft >= currentMaxScroll - 1;
};

const scrollByDirection = (direction: -1 | 1) => {
  if (!trackRef.value) {
    return;
  }

  trackRef.value.scrollBy({
    left: direction * props.scrollAmount,
    behavior: 'smooth',
  });

  clearScrollUpdateTimeout();
  scrollUpdateTimeout = window.setTimeout(updateScrollState, 180);
};

let trackResizeObserver: ResizeObserver | null = null;
// Guard against the nextTick callback firing after the component has already unmounted,
// which would create a ResizeObserver that never gets cleaned up.
let isMounted = false;
// Tracks whether the scrollend listener was actually registered so onBeforeUnmount
// only removes it when it was added (avoids a no-op remove on early unmount).
let scrollEndAttached = false;

onMounted(() => {
  isMounted = true;

  // Wait for DOM layout before reading scroll state.
  // ResizeObserver keeps arrows in sync when the track width changes
  // (e.g., font loaded, sidebar toggled).
  // scrollend fires after momentum scrolling completes on mobile Safari.
  nextTick(() => {
    if (!isMounted) return;

    updateScrollState();

    if (trackRef.value && typeof ResizeObserver !== 'undefined') {
      trackResizeObserver = new ResizeObserver(() => {
        updateScrollState();
      });
      trackResizeObserver.observe(trackRef.value);
    }
    if (trackRef.value) {
      trackRef.value.addEventListener('scrollend', updateScrollState);
      scrollEndAttached = true;
    }
  });

  window.addEventListener('resize', updateScrollState);
});

onBeforeUnmount(() => {
  isMounted = false;
  clearScrollUpdateTimeout();
  trackResizeObserver?.disconnect();
  trackResizeObserver = null;
  if (scrollEndAttached) {
    trackRef.value?.removeEventListener('scrollend', updateScrollState);
    scrollEndAttached = false;
  }
  window.removeEventListener('resize', updateScrollState);
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_ctx.showArrows)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "wc-carousel-modern__arrow wc-carousel-modern__arrow--previous",
            type: "button",
            disabled: atStart.value,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (scrollByDirection(-1)))
          }, [
            _createVNode(_component_font_awesome_icon, {
              icon: "chevron-left",
              "aria-hidden": "true"
            })
          ], 8, _hoisted_3))
        : _createCommentVNode("", true),
      _cache[2] || (_cache[2] = _createTextVNode()),
      _createElementVNode("ul", {
        ref_key: "trackRef",
        ref: trackRef,
        class: "wc-carousel-modern__track",
        "aria-label": _ctx.ariaLabel || _ctx.title,
        onScrollPassive: updateScrollState
      }, [
        _renderSlot(_ctx.$slots, "default")
      ], 40, _hoisted_4),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (_ctx.showArrows)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            class: "wc-carousel-modern__arrow wc-carousel-modern__arrow--next",
            type: "button",
            disabled: atEnd.value,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (scrollByDirection(1)))
          }, [
            _createVNode(_component_font_awesome_icon, {
              icon: "chevron-right",
              "aria-hidden": "true"
            })
          ], 8, _hoisted_5))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})